import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

// Standard 31-band 1/3-octave ISO frequencies
const BAND_FREQS = [
  20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 500,
  630, 800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000, 6300, 8000,
  10000, 12500, 16000, 20000,
];
const NUM_BANDS = 31;
// Q factor for 1/3-octave peaking filters
const GEQ_Q = 2.145;
const FS = 48000;

// Normalized [0,1] → dB (±15 dB range, 0.5 = 0 dB)
function normToDb(n) {
  return (Math.max(0, Math.min(1, n)) - 0.5) * 30;
}

function formatHz(hz) {
  if (hz >= 1000) return (hz / 1000).toFixed(hz >= 10000 ? 1 : 2).replace(/\.?0+$/, '') + 'k';
  return Math.round(hz) + '';
}

function peakingCoeffs(freqHz, gainDb, q) {
  const w0 = 2 * Math.PI * freqHz / FS;
  const A = Math.pow(10, gainDb / 40);
  const alpha = Math.sin(w0) / (2 * Math.max(0.01, q));
  const a0 = 1 + alpha / A;
  return {
    b0: (1 + alpha * A) / a0,
    b1: (-2 * Math.cos(w0)) / a0,
    b2: (1 - alpha * A) / a0,
    a1: (-2 * Math.cos(w0)) / a0,
    a2: (1 - alpha / A) / a0,
  };
}

function biquadMagDb({ b0, b1, b2, a1, a2 }, w) {
  const cos1 = Math.cos(w), cos2 = Math.cos(2 * w);
  const sin1 = Math.sin(w), sin2 = Math.sin(2 * w);
  const numRe = b0 + b1 * cos1 + b2 * cos2;
  const numIm = -b1 * sin1 - b2 * sin2;
  const denRe = 1 + a1 * cos1 + a2 * cos2;
  const denIm = -a1 * sin1 - a2 * sin2;
  const numSq = numRe * numRe + numIm * numIm;
  const denSq = denRe * denRe + denIm * denIm;
  return 10 * Math.log10(Math.max(1e-20, numSq / Math.max(1e-20, denSq)));
}

function computeGraphicCurve(gains, numPoints) {
  const result = new Float32Array(numPoints);
  for (let i = 0; i < numPoints; i++) {
    const hz = 20 * Math.pow(10, (i / (numPoints - 1)) * 3);
    const w = 2 * Math.PI * hz / FS;
    let totalDb = 0;
    for (let b = 0; b < NUM_BANDS; b++) {
      const gainDb = normToDb(gains[b]);
      if (Math.abs(gainDb) < 0.01) continue;
      totalDb += biquadMagDb(peakingCoeffs(BAND_FREQS[b], gainDb, GEQ_Q), w);
    }
    result[i] = totalDb;
  }
  return result;
}

// ---- Frequency Response Canvas ----
function GEQCanvas({ gainsL, gainsR, bypass }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.width;
    const H = canvas.height;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#0d0d0d';
    ctx.fillRect(0, 0, W, H);

    const DB_MIN = -18, DB_MAX = 18;
    const freqToX = (hz) => Math.log10(hz / 20) / 3 * W;
    const dbToY = (db) => H * (1 - (db - DB_MIN) / (DB_MAX - DB_MIN));

    const freqMarkers = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
    const dbMarkers = [-15, -12, -9, -6, -3, 0, 3, 6, 9, 12, 15];

    freqMarkers.forEach((hz) => {
      const x = freqToX(hz);
      ctx.strokeStyle = '#1e1e1e';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      ctx.fillStyle = '#444';
      ctx.font = '10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(formatHz(hz), x, H - 2);
    });

    dbMarkers.forEach((db) => {
      const y = dbToY(db);
      ctx.strokeStyle = db === 0 ? '#383838' : '#1e1e1e';
      ctx.lineWidth = db === 0 ? 1.5 : 1;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      if (db !== DB_MIN && db !== DB_MAX) {
        ctx.fillStyle = db === 0 ? '#666' : '#393939';
        ctx.font = '9px monospace';
        ctx.textAlign = 'left';
        ctx.fillText((db > 0 ? '+' : '') + db + 'dB', 2, y - 2);
      }
    });

    const zeroY = dbToY(0);

    if (bypass) {
      ctx.beginPath(); ctx.moveTo(0, zeroY); ctx.lineTo(W, zeroY);
      ctx.strokeStyle = '#555'; ctx.lineWidth = 2; ctx.stroke();
    } else {
      // L channel (yellow)
      const curveL = computeGraphicCurve(gainsL, W);
      ctx.beginPath();
      for (let i = 0; i < W; i++) {
        const y = dbToY(Math.max(DB_MIN, Math.min(DB_MAX, curveL[i])));
        if (i === 0) ctx.moveTo(i, y); else ctx.lineTo(i, y);
      }
      ctx.lineTo(W, zeroY); ctx.lineTo(0, zeroY); ctx.closePath();
      ctx.fillStyle = 'rgba(240,192,64,0.10)';
      ctx.fill();

      ctx.beginPath();
      for (let i = 0; i < W; i++) {
        const y = dbToY(Math.max(DB_MIN, Math.min(DB_MAX, curveL[i])));
        if (i === 0) ctx.moveTo(i, y); else ctx.lineTo(i, y);
      }
      ctx.strokeStyle = '#f0c040';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // R channel (blue)
      const curveR = computeGraphicCurve(gainsR, W);
      ctx.beginPath();
      for (let i = 0; i < W; i++) {
        const y = dbToY(Math.max(DB_MIN, Math.min(DB_MAX, curveR[i])));
        if (i === 0) ctx.moveTo(i, y); else ctx.lineTo(i, y);
      }
      ctx.lineTo(W, zeroY); ctx.lineTo(0, zeroY); ctx.closePath();
      ctx.fillStyle = 'rgba(126,200,227,0.08)';
      ctx.fill();

      ctx.beginPath();
      for (let i = 0; i < W; i++) {
        const y = dbToY(Math.max(DB_MIN, Math.min(DB_MAX, curveR[i])));
        if (i === 0) ctx.moveTo(i, y); else ctx.lineTo(i, y);
      }
      ctx.strokeStyle = '#7ec8e3';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }, [gainsL, gainsR, bypass]);

  return (
    <canvas
      ref={canvasRef}
      width={700}
      height={150}
      style={{ width: '100%', height: '150px', display: 'block', borderRadius: '4px', border: '1px solid #2a2a2a' }}
    />
  );
}

// ---- Main Master EQ Dialog ----
export default function MasterEQDialog({ open, onClose, read, write }) {
  const [bypass, setBypass] = useState(false);
  const [stereoLink, setStereoLink] = useState(false);
  const [gainsL, setGainsL] = useState(() => Array(NUM_BANDS).fill(0.5));
  const [gainsR, setGainsR] = useState(() => Array(NUM_BANDS).fill(0.5));

  // Load values from snapshot when dialog opens
  useEffect(() => {
    if (!open) return;
    setBypass(String(read('eq.bypass')) === '1');
    const l = Array.from({ length: NUM_BANDS }, (_, i) => {
      const v = parseFloat(read(`eq.peak.l.${i}`));
      return isNaN(v) ? 0.5 : v;
    });
    const r = Array.from({ length: NUM_BANDS }, (_, i) => {
      const v = parseFloat(read(`eq.peak.r.${i}`));
      return isNaN(v) ? 0.5 : v;
    });
    setGainsL(l);
    setGainsR(r);
    // Auto-detect stereo link: L and R match
    setStereoLink(l.every((v, i) => Math.abs(v - r[i]) < 1e-9));
  }, [open]); // intentionally only re-run when dialog opens/closes

  function handleGainL(bandIdx, val) {
    setGainsL((prev) => { const next = prev.slice(); next[bandIdx] = val; return next; });
    write(`eq.peak.l.${bandIdx}`, val);
    if (stereoLink) {
      setGainsR((prev) => { const next = prev.slice(); next[bandIdx] = val; return next; });
      write(`eq.peak.r.${bandIdx}`, val);
    }
  }

  function handleGainR(bandIdx, val) {
    setGainsR((prev) => { const next = prev.slice(); next[bandIdx] = val; return next; });
    write(`eq.peak.r.${bandIdx}`, val);
    if (stereoLink) {
      setGainsL((prev) => { const next = prev.slice(); next[bandIdx] = val; return next; });
      write(`eq.peak.l.${bandIdx}`, val);
    }
  }

  function handleBypass(v) {
    setBypass(v);
    write('eq.bypass', v ? 1 : 0);
  }

  function handleReset() {
    const flat = Array(NUM_BANDS).fill(0.5);
    setGainsL(flat);
    setGainsR(flat);
    for (let i = 0; i < NUM_BANDS; i++) {
      write(`eq.peak.l.${i}`, 0.5);
      write(`eq.peak.r.${i}`, 0.5);
    }
  }

  if (!open) return null;

  const channels = [
    { key: 'L', color: '#f0c040', gains: gainsL, handleGain: handleGainL },
    { key: 'R', color: '#7ec8e3', gains: gainsR, handleGain: handleGainR },
  ];

  return ReactDOM.createPortal(
    <div className="eq-overlay" onClick={onClose}>
      <div className="eq-dialog geq-dialog" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="eq-header">
          <span className="eq-title">EQ — MASTER (Graphic Stereo)</span>
          <div className="eq-header-right">
            <label className="eq-toggle">
              <input type="checkbox" checked={bypass} onChange={(e) => handleBypass(e.target.checked)} />
              <span>BYPASS</span>
            </label>
            <label className="eq-toggle">
              <input type="checkbox" checked={stereoLink} onChange={(e) => setStereoLink(e.target.checked)} />
              <span>STEREO LINK</span>
            </label>
            <button className="eq-btn" onClick={handleReset} title="Reset all bands to 0 dB">RESET</button>
            <button className="eq-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Frequency response canvas */}
        <div className="eq-canvas-wrap">
          <GEQCanvas gainsL={gainsL} gainsR={gainsR} bypass={bypass} />
        </div>

        {/* Stereo band sliders */}
        <div className="geq-bands">
          {channels.map(({ key, color, gains, handleGain }) => (
            <div key={key} className="geq-channel-row">
              <span className="geq-ch-label" style={{ color }}>{key}</span>
              <div className="geq-sliders">
                {BAND_FREQS.map((hz, i) => {
                  const db = normToDb(gains[i]);
                  const dbStr = (db >= 0 ? '+' : '') + db.toFixed(1);
                  return (
                    <div key={i} className="geq-band-col">
                      <span
                        className="geq-db-val"
                        style={{ color: Math.abs(db) < 0.3 ? '#444' : db > 0 ? '#f0c040' : '#7ec8e3' }}
                      >
                        {dbStr}
                      </span>
                      <div className="geq-slider-wrap">
                        <input
                          type="range"
                          min={0} max={1} step={0.001}
                          value={gains[i]}
                          onChange={(e) => handleGain(i, parseFloat(e.target.value))}
                          onDoubleClick={() => handleGain(i, 0.5)}
                          className="geq-slider"
                          title={`${formatHz(hz)}: ${dbStr} dB`}
                        />
                      </div>
                      <span className="geq-freq-label">{formatHz(hz)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="geq-legend">
          <span className="geq-legend-item" style={{ color: '#f0c040' }}>● L channel</span>
          <span className="geq-legend-item" style={{ color: '#7ec8e3' }}>● R channel</span>
          <span className="geq-legend-item" style={{ color: '#555' }}>Double-click a slider to reset band to 0 dB</span>
        </div>

      </div>
    </div>,
    document.body
  );
}
